import * as React from 'react';
import { useState, useLayoutEffect as useLayoutEffect$1 } from 'react';

/**
 * Creates a named context, provider, and hook.
 *
 * @param options create context options
 */
function createContext(options) {
    if (options === void 0) { options = {}; }
    var _a = options.strict, strict = _a === void 0 ? true : _a, _b = options.errorMessage, errorMessage = _b === void 0 ? 'useContext: `context` is undefined. Seems you forgot to wrap component within the Provider' : _b, name = options.name;
    var Context = React.createContext(undefined);
    Context.displayName = name;
    function useContext() {
        var _a;
        var context = React.useContext(Context);
        if (!context && strict) {
            var error = new Error(errorMessage);
            error.name = 'ContextError';
            (_a = Error.captureStackTrace) === null || _a === void 0 ? void 0 : _a.call(Error, error, useContext);
            throw error;
        }
        return context;
    }
    return [
        Context.Provider,
        useContext,
        Context,
    ];
}

/**
 * On the server, React emits a warning when calling `useLayoutEffect`.
 * This is because neither `useLayoutEffect` nor `useEffect` run on the server.
 * We use this safe version which suppresses the warning by replacing it with a noop on the server.
 *
 * See: https://reactjs.org/docs/hooks-reference.html#uselayouteffect
 */
var useLayoutEffect = Boolean(globalThis === null || globalThis === void 0 ? void 0 : globalThis.document)
    ? React.useLayoutEffect
    : function () { };

// We `toString()` to prevent bundlers from trying to `import { useId } from 'react';`
var useReactId = React['useId'.toString()] || (function () { return undefined; });
var count = 0;
var useId = function useId(deterministicId) {
    var _a = React.useState(useReactId()), id = _a[0], setId = _a[1];
    // React versions older than 18 will have client-side ids only.
    useLayoutEffect(function () {
        if (!deterministicId)
            setId(function (reactId) { return reactId !== null && reactId !== void 0 ? reactId : String(count++); });
    }, [deterministicId]);
    return deterministicId || (id ? "wartek-".concat(id) : '');
};

var isBrowser = typeof window !== 'undefined';

var getInitialState = function (query, defaultState) {
    // Prevent a React hydration mismatch when a default value is provided by not defaulting to window.matchMedia(query).matches.
    if (defaultState !== undefined) {
        return defaultState;
    }
    if (isBrowser) {
        return window.matchMedia(query).matches;
    }
    // A default value has not been provided, and you are rendering on the server, warn of a possible hydration mismatch when defaulting to false.
    if (process.env.NODE_ENV !== 'production') {
        console.warn('`useMedia` When server side rendering, defaultState should be defined to prevent a hydration mismatches.');
    }
    return false;
};
var useMedia = function (query, defaultState) {
    var _a = React.useState(getInitialState(query, defaultState)), state = _a[0], setState = _a[1];
    React.useEffect(function () {
        var mounted = true;
        var mql = window.matchMedia(query);
        var onChange = function () {
            if (!mounted) {
                return;
            }
            setState(!!mql.matches);
        };
        mql.addListener(onChange);
        setState(mql.matches);
        return function () {
            mounted = false;
            mql.removeListener(onChange);
        };
    }, [query]);
    return state;
};

var useDisclosure = function (props) {
    if (props === void 0) { props = {}; }
    var onClose = props.onClose, onOpen = props.onOpen, isOpen = props.isOpen;
    var _a = React.useState(isOpen || false), isOpenState = _a[0], setIsOpen = _a[1];
    var onCloseAction = React.useCallback(function () {
        setIsOpen(false);
        onClose && onClose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    var onOpenAction = React.useCallback(function () {
        setIsOpen(true);
        onOpen && onOpen();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    var onToggle = React.useCallback(function () {
        var action = isOpenState ? onCloseAction : onOpenAction;
        action && action();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpenState]);
    return {
        isOpen: !!isOpenState,
        onOpen: onOpen,
        onClose: onClose,
        onToggle: onToggle,
    };
};

var useWindowSize = function () {
    var _a = useState({
        innerWidth: 0,
        innerHeight: 0,
    }), windowSize = _a[0], setWindowSize = _a[1];
    useLayoutEffect$1(function () {
        var updateSize = function () {
            setWindowSize({
                innerWidth: window.innerWidth,
                innerHeight: window.innerHeight,
            });
        };
        updateSize();
        window === null || window === void 0 ? void 0 : window.addEventListener('resize', updateSize);
        return function () { return window === null || window === void 0 ? void 0 : window.removeEventListener('resize', updateSize); };
    }, []);
    return windowSize;
};

export { createContext, isBrowser, useDisclosure, useId, useLayoutEffect, useMedia, useWindowSize };
